/**
 * Default (suggested) Club program name, localized to the RESTAURANT's
 * audience — the menu's main language (restaurant.languageId) — NOT the
 * admin UI locale. The club name is customer-facing: it renders inside the
 * digital menu, whose default display language is exactly languageId, so
 * the suggestion must match that language even when the owner manages the
 * panel in another one (e.g. a zh-locale admin of a Spanish restaurant).
 *
 * Kept as a domain map (mirroring club-currency-defaults) because the UI
 * i18n catalogs are keyed by admin locale and cannot be sync-read for an
 * arbitrary language. A unit test enforces one entry per admin catalog.
 */
export const CLUB_DEFAULT_NAME_TEMPLATES: Record<string, string> = {
  ar: 'نادي {restaurant}',
  bn: 'ক্লাব {restaurant}',
  ca: 'Club {restaurant}',
  cs: 'Klub {restaurant}',
  de: 'Club {restaurant}',
  en: 'Club {restaurant}',
  es: 'Club {restaurant}',
  fi: 'Club {restaurant}',
  fr: 'Club {restaurant}',
  hi: 'क्लब {restaurant}',
  id: 'Club {restaurant}',
  it: 'Club {restaurant}',
  ja: 'クラブ {restaurant}',
  pt: 'Clube {restaurant}',
  ru: 'Клуб {restaurant}',
  tr: 'Club {restaurant}',
  ur: 'کلب {restaurant}',
  vi: 'Club {restaurant}',
  zh: '{restaurant}会员俱乐部',
};

// "Club {restaurant}" reads fine for the rare unmapped menu languages in
// production (an, he) and for restaurants with no languageId loaded yet.
const FALLBACK_TEMPLATE = CLUB_DEFAULT_NAME_TEMPLATES.es;

export function getClubDefaultName(
  menuLanguageId: string | null | undefined,
  restaurantName: string
): string {
  // language_id may carry a region suffix ("pt-BR"); the base language decides.
  const base = (menuLanguageId ?? '').toLowerCase().split('-')[0];
  const template = CLUB_DEFAULT_NAME_TEMPLATES[base] ?? FALLBACK_TEMPLATE;
  return template.replace('{restaurant}', restaurantName).trim();
}
